import classNames from 'classnames';
import React, { useMemo } from 'react';

import { format, getZonedDate } from '@eika-infrastruktur/date';
import { Button } from '@eika/button';
import { useViewType, ViewType } from '@eika/hooks';
import { IconTypes } from '@eika/icon';
import { Label } from '@eika/label';

import type { Account } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';
import { LoanViewActions } from '../../widgets/LoanViewActions';
import AccountViewSkeleton from '../AccountView/AccountViewSkeleton';
import { ErrorCardWrapper } from '../shared/ErrorCardWrapper';
import { currencyFormatter } from '../shared/numberFormat';
import LoanViewSkeleton from './LoanViewSkeleton';
import useLoans from './useLoansView';

import '../AccountView/AccountView.scss';
import '../AccountView/balance/AccountBalanceView.scss';
import './LoanView.scss';

export interface Props {
    account?: Account;
    accountIsLoading?: boolean;
}

const LoanView: React.FC<Props> = ({ account, accountIsLoading = false }) => {
    const translated = useTranslation();

    const hasLoanDetails = account?.additionalInfo.hasLoanDetails === true;
    const {
        showLoanDetails,
        isLoading: loanDetailsLoading,
        loanDetails,
        interest,
        error,
        refetch,
    } = useLoans(account?.accountId, hasLoanDetails);

    const isLoading = loanDetailsLoading || accountIsLoading;

    const viewType = useViewType();
    const isMobile = viewType !== ViewType.desktop;
    const isDesktop = viewType === ViewType.desktop;

    const DATE_FORMAT = useMemo(() => {
        const today = getZonedDate();
        if (getZonedDate(loanDetails?.nextPaymentDate).getFullYear() !== today.getFullYear()) {
            return 'd. MMMM yyyy';
        }

        return 'd. MMMM';
    }, [loanDetails?.nextPaymentDate]);

    if (isLoading) {
        return (
            <>
                <AccountViewSkeleton withInterest>
                    <LoanViewSkeleton />
                </AccountViewSkeleton>
            </>
        );
    }

    if (!account) {
        return null;
    }

    const isDownpaid = error?.errorName === 'LOAN_WITH_POSITIVE_BALANCE' || !hasLoanDetails;

    if (error && !isDownpaid) {
        return (
            <ErrorCardWrapper exception={error} defaultError={translated.loanError}>
                <Button onClick={refetch}>{translated.tryAgain}</Button>
            </ErrorCardWrapper>
        );
    }

    const hasPaymentAmount = typeof loanDetails?.nextPaymentAmount === 'number' && loanDetails.nextPaymentAmount >= 0;
    const isLargeAmount = (amount: number | undefined) => (amount ?? 0) >= 1000000 || (amount ?? 0) <= -10000;

    const { accountingBalance } = account.balance;
    return (
        <div className="sgw-account-details loan-account-details">
            <section className="sgw-account-details__meta account-view__balance-container">
                <dl className="sgw-account-details__wrapper" itemScope itemType="https://schema.org/MortgageLoan">
                    <dt className="screen-reader-text">{translated.accountView.disposible}</dt>
                    <dd
                        className="account-details__amount"
                        itemProp="amount"
                        itemScope
                        itemType="https://schema.org/MonetaryAmount"
                    >
                        <span
                            itemProp="value"
                            {...{ content: String(accountingBalance) }}
                            className={classNames({
                                'large-balance': isLargeAmount(accountingBalance),
                            })}
                        >
                            {currencyFormatter(accountingBalance, account.currency)}
                        </span>
                        <meta itemProp="currency" content={account.currency.toString()} />
                        {isDownpaid && (
                            <Label
                                className="account-details__loan-downpaid"
                                type={isDesktop ? 'inverted' : 'success'}
                                icon={IconTypes.USP_CHECKMARK_24}
                                text={translated.loanDownpaid}
                            />
                        )}
                    </dd>
                    {showLoanDetails && (
                        <section className="loan-details">
                            {interest && (
                                <dl className="loan-details__content">
                                    <dt className="loan-view-interest__label">{translated.loanDetails.interest}</dt>
                                    <dd className="loan-view-interest__value" itemProp="interest" itemScope>
                                        {interest.rate} %
                                    </dd>
                                </dl>
                            )}
                            {loanDetails && hasPaymentAmount && (
                                <>
                                    <dl className="loan-details__content">
                                        <dt className="loan-view-payment-date__label">
                                            {translated.loanDetails.nextPayment}
                                        </dt>
                                        <dd className="loan-view-payment-date__value" itemProp="payment-date" itemScope>
                                            {format(getZonedDate(loanDetails.nextPaymentDate), DATE_FORMAT)}
                                        </dd>
                                    </dl>
                                    <dl className="loan-details__content">
                                        <dt className="loan-view-payment-date__label">
                                            {translated.loanDetails.amount}
                                        </dt>
                                        <dd className="loan-view-payment-date__value" itemProp="payment-date" itemScope>
                                            {currencyFormatter(loanDetails?.nextPaymentAmount ?? 0, account.currency)}
                                        </dd>
                                    </dl>
                                </>
                            )}
                        </section>
                    )}
                </dl>
                <div className="background-wrapper loan">
                    <div className="background" />
                </div>
            </section>
            {isMobile && showLoanDetails && <LoanViewActions />}
        </div>
    );
};

export default LoanView;
